import React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  XIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpRight,
  Blocks,
  Grid2X2,
  LayoutPanelTop,
  LayoutPanelLeft,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import type { JsxElement } from "@/utils/jsxElementTypes";
import { useImagePreview } from "@/hooks/useImagePreview";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { setDraggingComponent } from "@/store/iframeSlice";

interface DetailsPanelProps {
  component: JsxElement;
  variants: JsxElement[];
  onClose: () => void;
  onAddToCanvas: (variant?: JsxElement) => void;
}

// Define variants and transition outside the return statement for clarity
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? "100%" : "-100%",
    opacity: 0,
  }),
};

const slideTransition = {
  x: { type: "tween" },
  opacity: { duration: 0.2 },
};

export const DetailsPanel = React.memo(
  ({ component, variants, onClose, onAddToCanvas }: DetailsPanelProps) => {
    const [currentVariantIndex, setCurrentVariantIndex] = useState<number>(0);
    const totalItems = variants.length > 0 ? variants.length : 1;
    const currentItem =
      variants.length > 0 ? variants[currentVariantIndex] : component;
    const [direction, setDirection] = useState(0); // 0 = initial, 1 = next, -1 = prev
    const [PlaceholderIcon, setPlaceholderIcon] = useState<React.ComponentType<
      React.SVGProps<SVGSVGElement>
    > | null>(null);

    // Track image loading states locally to avoid flicker
    const [imageLoadStates, setImageLoadStates] = useState<{
      [key: string]: "loading" | "loaded" | "error";
    }>({});

    // Get image previews for the main asset and all variants
    const mainAssetImage = useImagePreview(component);
    const variantImages = variants.map((variant) => useImagePreview(variant));

    // Get the current image and loading state to display
    const currentImageData =
      variants.length > 0 ? variantImages[currentVariantIndex] : mainAssetImage;

    const currentImage = currentImageData.imagePath;
    const currentImageKey = `${currentItem.codebaseId}_${currentVariantIndex}_${currentImage}`;

    // Use consistent motion key based only on variant index, not content type
    const motionKey = `variant-${currentVariantIndex}`;

    useEffect(() => {
      setCurrentVariantIndex(0);
      setImageLoadStates({});
    }, [component.key]);

    // Initialize loading state when image URL appears
    useEffect(() => {
      if (currentImage && !(currentImageKey in imageLoadStates)) {
        setImageLoadStates((prev) => ({
          ...prev,
          [currentImageKey]: "loading",
        }));
      }
    }, [currentImage, currentImageKey, imageLoadStates]);

    const previewContainerRef = useRef<HTMLDivElement>(null);

    const handleImageLoad = useCallback((imageKey: string) => {
      setImageLoadStates((prev) => ({ ...prev, [imageKey]: "loaded" }));
    }, []);

    const handleImageError = useCallback((imageKey: string) => {
      setImageLoadStates((prev) => ({ ...prev, [imageKey]: "error" }));
    }, []);

    const handleImageLoadStart = useCallback((imageKey: string) => {
      setImageLoadStates((prev) => ({ ...prev, [imageKey]: "loading" }));
    }, []);

    // Show placeholder if no image URL or if the image is confirmed broken
    const shouldShowPlaceholder =
      !currentImage || imageLoadStates[currentImageKey] === "error";

    // Show loading spinner only if we have an image URL and haven't tested it yet
    const isImageLoading =
      currentImage &&
      !(currentImageKey in imageLoadStates) &&
      !shouldShowPlaceholder;

    const wheelDelta = useRef(0);
    const lastWheelTime = useRef(0);
    const isTrackpad = useRef<boolean | null>(null);

    const handleNextVariant = useCallback(() => {
      setDirection(1);
      setCurrentVariantIndex((prevIndex) =>
        Math.min(prevIndex + 1, totalItems - 1)
      );
    }, [totalItems]);

    const handlePrevVariant = useCallback(() => {
      setDirection(-1);
      setCurrentVariantIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    }, []);

    const handleWheel = useCallback(
      (e: React.WheelEvent) => {
        // Only handle scroll if there are multiple variants
        if (variants.length <= 1) return;

        e.stopPropagation();

        const now = performance.now();
        const timeDelta = now - lastWheelTime.current;
        lastWheelTime.current = now;

        // Detect input device based on timing and delta
        if (isTrackpad.current === null) {
          // Trackpads typically have small deltas (< 50) and quick succession (< 50ms)
          isTrackpad.current = Math.abs(e.deltaY) < 50 && timeDelta < 50;
        }

        // Different thresholds for different input devices
        const threshold = isTrackpad.current ? 100 : 0;

        // Accumulate scroll delta
        wheelDelta.current += Math.abs(e.deltaY);

        // Only trigger variant change when threshold is reached
        if (wheelDelta.current >= threshold) {
          if (e.deltaY > 0) {
            handleNextVariant();
          } else {
            handlePrevVariant();
          }
          wheelDelta.current = 0; // Reset accumulator
        }

        // Reset device detection after a pause in scrolling
        if (timeDelta > 1000) {
          isTrackpad.current = null;
        }
      },
      [variants.length, handleNextVariant, handlePrevVariant]
    );
    const setCanvasComponentKey = useCanvasStore(
      (store) => store.setCanvasComponentKey
    );

    const handleLinkOut = () => {
      setCanvasComponentKey(component.key);

      onClose();
    };

    const dispatch = useDispatch();

    // Select random placeholder icon on mount
    useEffect(() => {
      const placeholderIcons: React.ComponentType<
        React.SVGProps<SVGSVGElement>
      >[] = [Blocks, Grid2X2, LayoutPanelTop, LayoutPanelLeft];
      setPlaceholderIcon(
        () =>
          placeholderIcons[Math.floor(Math.random() * placeholderIcons.length)]
      );
    }, []);

    const handleDragStart = useCallback(
      (e: React.DragEvent, variant: JsxElement) => {
        e.dataTransfer.dropEffect = "move";
        if (!variant.codebaseId) {
          toast.error("Could not find element in codebase");
          return;
        }

        if (!variant.storybookSourceStart) {
          toast.error("Could not parse storybook source code");
          return;
        }

        dispatch(setDraggingComponent(variant.type));
        e.dataTransfer.setData("tempo/codebaseId", variant.codebaseId);
        if (variant.isTemplateStory) {
          e.dataTransfer.setData(
            "tempo/isTemplateStory",
            variant.isTemplateStory.toString()
          );
        }
      },
      [dispatch]
    );

    return (
      <div className="w-[200px] h-full flex flex-col bg-background-primary border border-border-primary flex-shrink-0 shadow-xl sticky rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-2 pl-4 border-b border-secondary-300 flex-shrink-0">
          <h2 className="text-xs font-medium text-text-primary">Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Preview Area */}
        <div className="p-4 flex-grow flex flex-col min-h-0">
          <div
            ref={previewContainerRef}
            className="relative bg-secondary-200 rounded-lg aspect-square flex items-center justify-center p-1 overflow-hidden h-full w-full group cursor-pointer"
            draggable={true}
            onDragStart={(e) => handleDragStart(e, currentItem)}
            onDragEnd={() => {
              dispatch(setDraggingComponent(null));
            }}
            onWheel={handleWheel}
          >
            {/* Display the current image */}
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={motionKey}
                custom={direction}
                variants={slideVariants}
                initial={"enter"}
                animate="center"
                exit="exit"
                transition={slideTransition}
                className="absolute inset-0 flex items-center justify-center p-1"
              >
                {currentImage &&
                imageLoadStates[currentImageKey] === "loaded" ? (
                  <div className="rounded-md overflow-hidden w-min-content h-min-content content-center">
                    <img
                      key={currentImage}
                      src={currentImage}
                      alt={currentItem.componentName}
                      className="object-scale-down rounded-sm max-w-full max-h-full"
                      onLoad={() => handleImageLoad(currentImageKey)}
                      onError={() => handleImageError(currentImageKey)}
                      onLoadStart={() => handleImageLoadStart(currentImageKey)}
                    />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-text-secondary">
                    {isImageLoading ? (
                      <div className="w-16 h-16 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400" />
                      </div>
                    ) : (
                      PlaceholderIcon && (
                        <PlaceholderIcon className="w-16 h-16 text-gray-400" />
                      )
                    )}
                  </div>
                )}
                {/* Hidden image for loading/error detection */}
                {currentImage &&
                  imageLoadStates[currentImageKey] !== "loaded" && (
                    <img
                      src={currentImage}
                      alt=""
                      className="hidden"
                      onLoad={() => handleImageLoad(currentImageKey)}
                      onError={() => handleImageError(currentImageKey)}
                      onLoadStart={() => handleImageLoadStart(currentImageKey)}
                    />
                  )}
              </motion.div>
            </AnimatePresence>

            {variants.length > 1 && (
              <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={handlePrevVariant}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-secondary-300/50 hover:bg-secondary-300/80 rounded-md text-text-primary disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto"
                  disabled={currentVariantIndex === 0}
                >
                  <ArrowLeftIcon className="p-1 w-6 h-6 rounded-sm bg-background-primary text-text-primary shadow-xs pointer-events-auto visible hover:bg-background-secondary active:bg-background-secondary focus-visible:ring-4 focus-visible:ring-[hsl(var(--blue-60)/50%)] transition-colors ease-out" />
                </button>
                <button
                  type="button"
                  onClick={handleNextVariant}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-1 bg-secondary-300/50 hover:bg-secondary-300/80 rounded-md text-text-primary disabled:opacity-50 disabled:cursor-not-allowed pointer-events-auto"
                  disabled={currentVariantIndex === totalItems - 1}
                >
                  <ArrowRightIcon className="p-1 w-6 h-6 rounded-sm bg-background-primary text-text-primary shadow-xs pointer-events-auto visible hover:bg-background-secondary active:bg-background-secondary focus-visible:ring-4 focus-visible:ring-[hsl(var(--blue-60)/50%)] transition-colors ease-out" />
                </button>

                <div className="absolute top-4 right-4 z-10 flex space-x-1.5 pointer-events-auto">
                  {Array.from({ length: totalItems }).map((_, index) => (
                    <button
                      key={`variant-dot-${component.key}-${index}`}
                      type="button"
                      className={cn(
                        "block w-1.5 h-1.5 rounded-full cursor-pointer",
                        index === currentVariantIndex
                          ? "bg-white"
                          : "bg-[#4B4B51]"
                      )}
                      onClick={() => {
                        // Determine direction based on index change
                        const newDirection =
                          index > currentVariantIndex ? 1 : -1;
                        setDirection(newDirection);
                        setCurrentVariantIndex(index);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="p-4 flex-grow flex flex-col min-h-0 border-t border-border-primary">
          {/* Label & Variants */}
          <div className="mb-4 flex-shrink-0">
            <div className="flex items-start text-text-primary font-medium mb-0.5 text-xs">
              <span className="text-wrap w-full break-words leading-tight">
                {component.componentName || "Component"}
                {variants.length > 0 &&
                  currentVariantIndex < variants.length && (
                    <span className="text-text-secondary">
                      &nbsp;({currentItem.componentName})
                    </span>
                  )}
              </span>
            </div>
            <div className="text-xs text-text-secondary flex items-center">
              <button
                type="button"
                onClick={handleLinkOut}
                className="text-text-secondary hover:text-text-primary py-1 pb-1.5 flex items-center gap-1 text-2xs"
                title="Link out"
              >
                <span>
                  {variants.length > 1
                    ? `${variants.length} Variants`
                    : "View Component"}
                </span>
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <Button
            onClick={() =>
              onAddToCanvas(variants.length > 0 ? currentItem : undefined)
            }
            className="w-full mt-auto bg-primary-0 hover:bg-primary-100 text-text-primary flex-shrink-0 text-xs"
          >
            Add to Canvas
          </Button>
        </div>
      </div>
    );
  }
);
